import useSWR from 'swr';

import { fetchEndpointData } from '@utils/fetchEndpointData';

import type { MinimalGeneralSettingsResponse } from './endpoints/MinimalGeneralSettingsEndpoint.types';
import type { AllGeneralSettingsResponse } from './endpoints/AllGeneralSettingsEndpoint.types';
import type { EmailParamsResponse } from './endpoints/EmailParamsEndpoint.types';
import type { CarrierInformationDocumentsResponse } from './endpoints/CarrierInformationDocumentsEndpoint.types';
import type { CarrierTermsResponse } from './endpoints/CarrierTermsEndpoint.types';
import type {
  LinkCarrierTermsDocumentBody,
  LinkCarrierTermsDocumentResponse,
} from './endpoints/LinkCarrierTermsDocumentEndpoint.types';
import type { UnlinkCarrierTermsDocumentResponse } from './endpoints/UnlinkCarrierTermsDocumentEndpoint.types';
import type {
  UpdateGeneralSettingsBody,
  UpdateGeneralSettingsResponse,
} from './endpoints/UpdateGeneralSettingsEndpoint.types';
import type { UpdateCounterBody, UpdateCounterResponse } from './endpoints/UpdateCounterEndpoint.types';
import type {
  UpdateMonthlyCounterBody,
  UpdateMonthlyCounterResponse,
} from './endpoints/UpdateMonthlyCounterEndpoint.types';
import type { UpdateEmailContentBody, UpdateEmailContentResponse } from './endpoints/UpdateEmailContentEndpoint.types';
import type { UpdateDocumentNoteBody, UpdateDocumentNoteResponse } from './endpoints/UpdateDocumentNoteEndpoint.types';
import type {
  LinkEmailAttachmentBody,
  LinkEmailAttachmentResponse,
} from './endpoints/LinkEmailAttachmentEndpoint.types';
import type { UnlinkEmailAttachmentResponse } from './endpoints/UnlinkEmailAttachmentEndpoint.types';
import type {
  LinkCarrierInformationDocumentBody,
  LinkCarrierInformationDocumentResponse,
} from './endpoints/LinkCarrierInformationDocumentEndpoint.types';
import type { UpdateCarrierInformationDocumentResponse } from './endpoints/UpdateCarrierInformationDocumentEndpoint.types';
import type { UnlinkCarrierInformationDocumentResponse } from './endpoints/UnlinkCarrierInformationDocumentEndpoint.types';
import type { CreateMailboxBody, CreateMailboxResponse } from './endpoints/CreateMailboxEndpoint.types';
import type { UpdateMailboxBody, UpdateMailboxResponse } from './endpoints/UpdateMailboxEndpoint.types';
import type { DeleteMailboxResponse } from './endpoints/DeleteMailboxEndpoint.types';
import type { SendDieselSurchargeNewsletterResponse } from './endpoints/SendDieselSurchargeNewsletterEndpoint.types';
import type { EmailTypeValue, LanguageValue, DocumentTypeValue } from './endpoints/shared.types';

// Cleaner re-exports for consumers.
export type MinimalGeneralSettings = MinimalGeneralSettingsResponse;
export type AllGeneralSettings = AllGeneralSettingsResponse;

// ─── SWR keys ────────────────────────────────────────────────────────
export const MINIMAL_GENERAL_SETTINGS_SWR_KEY = 'minimal-general-settings';
export const ALL_GENERAL_SETTINGS_SWR_KEY = 'all-general-settings';
export const EMAIL_PARAMS_SWR_KEY = 'email-params';
export const CARRIER_INFORMATION_DOCUMENTS_SWR_KEY = 'carrier-information-documents';
export const CARRIER_TERMS_SWR_KEY = 'carrier-terms';

// ─── Queries ─────────────────────────────────────────────────────────

export async function getMinimalGeneralSettings(): Promise<MinimalGeneralSettingsResponse> {
  return fetchEndpointData<MinimalGeneralSettingsResponse>('/api/v1/settings/general/minimal');
}

export function useMinimalGeneralSettings() {
  return useSWR(MINIMAL_GENERAL_SETTINGS_SWR_KEY, getMinimalGeneralSettings);
}

export async function getAllGeneralSettings(): Promise<AllGeneralSettingsResponse> {
  return fetchEndpointData<AllGeneralSettingsResponse>('/api/v1/settings/general');
}

export function useAllGeneralSettings() {
  return useSWR(ALL_GENERAL_SETTINGS_SWR_KEY, getAllGeneralSettings);
}

export async function getEmailParams(emailType: EmailTypeValue): Promise<EmailParamsResponse> {
  const params = new URLSearchParams();
  params.set('emailType', emailType);
  return fetchEndpointData<EmailParamsResponse>(`/api/v1/settings/email-params?${params.toString()}`);
}

export function useEmailParams(emailType: EmailTypeValue) {
  return useSWR([EMAIL_PARAMS_SWR_KEY, emailType], () => getEmailParams(emailType));
}

export async function getCarrierInformationDocuments(): Promise<CarrierInformationDocumentsResponse> {
  return fetchEndpointData<CarrierInformationDocumentsResponse>('/api/v1/settings/carrier-information-documents');
}

export function useCarrierInformationDocuments() {
  return useSWR(CARRIER_INFORMATION_DOCUMENTS_SWR_KEY, getCarrierInformationDocuments);
}

/**
 * General terms & conditions a carrier accepts when confirming a transport order,
 * in the given language (server falls back to EN / NL). Anonymous endpoint: also
 * usable from the confirmation link page.
 */
export async function getCarrierTerms(language?: string): Promise<CarrierTermsResponse> {
  const params = new URLSearchParams();
  if (language) {
    params.set('lang', language.toUpperCase());
  }
  const queryString = params.toString();
  return fetchEndpointData<CarrierTermsResponse>(`/api/v1/carrier-terms${queryString ? `?${queryString}` : ''}`);
}

/** Pass `null` to skip the fetch, e.g. for a carrier that doesn't have to accept the terms. */
export function useCarrierTerms(language?: string | null) {
  return useSWR(language === null ? null : [CARRIER_TERMS_SWR_KEY, language], () =>
    getCarrierTerms(language ?? undefined),
  );
}

// ─── Mutations ───────────────────────────────────────────────────────

export async function updateGeneralSettings(data: UpdateGeneralSettingsBody): Promise<UpdateGeneralSettingsResponse> {
  return fetchEndpointData<UpdateGeneralSettingsResponse>('/api/v1/settings/general', {
    method: 'PATCH',
    body: data,
  });
}

export async function updateCounter(body: UpdateCounterBody): Promise<UpdateCounterResponse> {
  return fetchEndpointData<UpdateCounterResponse>('/api/v1/settings/counters/update', {
    method: 'POST',
    body,
  });
}

export async function updateMonthlyCounter(body: UpdateMonthlyCounterBody): Promise<UpdateMonthlyCounterResponse> {
  return fetchEndpointData<UpdateMonthlyCounterResponse>('/api/v1/settings/counters/monthly/update', {
    method: 'POST',
    body,
  });
}

export async function updateEmailContent(body: UpdateEmailContentBody): Promise<UpdateEmailContentResponse> {
  return fetchEndpointData<UpdateEmailContentResponse>('/api/v1/settings/email-content', {
    method: 'POST',
    body,
  });
}

export async function updateDocumentNote(body: UpdateDocumentNoteBody): Promise<UpdateDocumentNoteResponse> {
  return fetchEndpointData<UpdateDocumentNoteResponse>('/api/v1/settings/document-note', {
    method: 'POST',
    body,
  });
}

export async function linkEmailAttachment(body: LinkEmailAttachmentBody): Promise<LinkEmailAttachmentResponse> {
  return fetchEndpointData<LinkEmailAttachmentResponse>('/api/v1/settings/email-attachments', {
    method: 'POST',
    body,
  });
}

export async function unlinkEmailAttachment(id: string): Promise<UnlinkEmailAttachmentResponse> {
  return fetchEndpointData<UnlinkEmailAttachmentResponse>(`/api/v1/settings/email-attachments/${id}`, {
    method: 'DELETE',
  });
}

export async function linkCarrierTermsDocument(
  body: LinkCarrierTermsDocumentBody,
): Promise<LinkCarrierTermsDocumentResponse> {
  return fetchEndpointData<LinkCarrierTermsDocumentResponse>('/api/v1/settings/carrier-terms-documents', {
    method: 'PUT',
    body,
  });
}

export async function unlinkCarrierTermsDocument(id: string): Promise<UnlinkCarrierTermsDocumentResponse> {
  return fetchEndpointData<UnlinkCarrierTermsDocumentResponse>(`/api/v1/settings/carrier-terms-documents/${id}`, {
    method: 'DELETE',
  });
}

export async function linkCarrierInformationDocument(
  body: LinkCarrierInformationDocumentBody,
): Promise<LinkCarrierInformationDocumentResponse> {
  return fetchEndpointData<LinkCarrierInformationDocumentResponse>('/api/v1/settings/carrier-information-documents', {
    method: 'POST',
    body,
  });
}

export async function updateCarrierInformationDocument(
  documentId: string,
  newName: string,
): Promise<UpdateCarrierInformationDocumentResponse> {
  return fetchEndpointData<UpdateCarrierInformationDocumentResponse>(
    `/api/v1/settings/carrier-information-documents/by-document/${documentId}`,
    {
      method: 'PATCH',
      body: { newName },
    },
  );
}

export async function unlinkCarrierInformationDocument(id: string): Promise<UnlinkCarrierInformationDocumentResponse> {
  return fetchEndpointData<UnlinkCarrierInformationDocumentResponse>(
    `/api/v1/settings/carrier-information-documents/${id}`,
    { method: 'DELETE' },
  );
}

export async function createMailbox(data: CreateMailboxBody): Promise<CreateMailboxResponse> {
  return fetchEndpointData<CreateMailboxResponse>('/api/v1/settings/mailboxes', {
    method: 'POST',
    body: data,
  });
}

export async function updateMailbox(mailboxId: string, data: UpdateMailboxBody): Promise<UpdateMailboxResponse> {
  return fetchEndpointData<UpdateMailboxResponse>(`/api/v1/settings/mailboxes/${mailboxId}`, {
    method: 'PATCH',
    body: data,
  });
}

export async function deleteMailbox(mailboxId: string): Promise<DeleteMailboxResponse> {
  return fetchEndpointData<DeleteMailboxResponse>(`/api/v1/settings/mailboxes/${mailboxId}`, {
    method: 'DELETE',
  });
}

export async function sendDieselSurchargeNewsletter(): Promise<SendDieselSurchargeNewsletterResponse> {
  return fetchEndpointData<SendDieselSurchargeNewsletterResponse>('/api/v1/settings/diesel-surcharge-newsletter/send', {
    method: 'POST',
  });
}

// Re-export wire enum value types for consumers.
export type { EmailTypeValue, LanguageValue, DocumentTypeValue };
